<template>
  <div>
    <div class="d-flex flex-wrap align-items-end mb-2 gap-1">
      <div class="mr-1">
        <label class="d-block mb-50">Időszak</label>
        <b-form-input
          v-model="period"
          type="month"
        />
      </div>
      <b-button
        variant="primary"
        class="mt-1"
        @click="loadData"
      >
        Frissítés
      </b-button>
    </div>

    <loading :status="loading" />

    <div v-if="!loading">
      <b-row>
        <b-col
          v-for="item in summaryCards"
          :key="item.key"
          cols="12"
          md="4"
          lg="3"
          class="mb-1"
        >
          <b-card>
            <div class="text-muted">
              {{ item.label }}
            </div>
            <h4 class="mb-0">
              {{ item.value }}
            </h4>
            <small class="text-muted d-block mt-25">
              Forrás: {{ item.sourceShort }}
            </small>
          </b-card>
        </b-col>
      </b-row>

      <b-card class="mb-2">
        <h4 class="mb-1">
          Adatforrás összefoglaló
        </h4>
        <b-table
          :items="sourceSummaryRows"
          :fields="sourceFields"
          striped
          responsive
          small
        />
      </b-card>

      <b-card class="mb-2">
        <h4 class="mb-1">
          Top kockázatok
        </h4>
        <b-table
          :items="overview.top_risks || []"
          :fields="topRiskFields"
          striped
          responsive
          small
        >
          <template #cell(risk_score)="data">
            <b-badge :variant="riskVariant(data.item.risk_level)">
              {{ data.item.risk_score }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="loadProject(data.item.project_id)"
            >
              Részletek
            </b-button>
          </template>
        </b-table>
      </b-card>

      <b-card class="mb-2">
        <h4 class="mb-1">
          Riasztások (50+ kockázat)
        </h4>
        <b-table
          :items="alerts"
          :fields="alertFields"
          striped
          responsive
          small
        >
          <template #cell(risk_score)="data">
            <b-badge :variant="riskVariant(data.item.risk_level)">
              {{ data.item.risk_score }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="loadProject(data.item.project_id)"
            >
              Részletek
            </b-button>
          </template>
        </b-table>
      </b-card>

    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BRow,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import Loading from '@/components/loading/Loading.vue'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BRow,
    BTable,
    Loading,
  },
  data() {
    return {
      loading: false,
      period: '',
      fixedProjectId: 166,
      overview: {
        summary: {},
        top_risks: [],
      },
      alerts: [],
      topRiskFields: [
        { key: 'project_name', label: 'Projekt' },
        { key: 'risk_score', label: 'Kockázat' },
        { key: 'risk_level', label: 'Szint' },
        { key: 'summary', label: 'Összefoglaló' },
        { key: 'actions', label: 'Művelet' },
      ],
      alertFields: [
        { key: 'project_name', label: 'Projekt' },
        { key: 'risk_score', label: 'Kockázat' },
        { key: 'risk_level', label: 'Szint' },
        { key: 'summary', label: 'Leírás' },
        { key: 'actions', label: 'Művelet' },
      ],
      sourceFields: [
        { key: 'kpi', label: 'KPI' },
        { key: 'short', label: 'Forrás' },
        { key: 'description', label: 'Leírás' },
      ],
    }
  },
  computed: {
    sourceSummaryRows() {
      const summary = this.overview?.sources?.summary || {}
      return Object.keys(summary).map(key => ({
        key,
        kpi: summary[key]?.label || key,
        short: summary[key]?.short || '-',
        description: summary[key]?.description || '-',
      }))
    },
    summaryCards() {
      const summary = this.overview.summary || {}
      return [
        { key: 'project_count', label: 'Projekt darabszám', value: summary.project_count || 0 },
        { key: 'avg_risk_score', label: 'Átlagos kockázat', value: summary.avg_risk_score || 0 },
        { key: 'total_plan_revenue', label: 'Tervezett bevétel', value: this.money(summary.total_plan_revenue) },
        { key: 'total_actual_revenue', label: 'Tényleges bevétel', value: this.money(summary.total_actual_revenue) },
        { key: 'total_plan_cost', label: 'Tervezett költség', value: this.money(summary.total_plan_cost) },
        { key: 'total_actual_cost', label: 'Tényleges költség', value: this.money(summary.total_actual_cost) },
        { key: 'total_margin_plan', label: 'Tervezett nyereség', value: this.money(summary.total_margin_plan) },
        { key: 'total_margin_actual', label: 'Tényleges nyereség', value: this.money(summary.total_margin_actual) },
      ].map(item => ({
        ...item,
        sourceShort: this.sourceForKpi(item.key).short,
      }))
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    loadData() {
      this.loading = true
      const params = [`project_id=${this.fixedProjectId}`]
      if (this.period) {
        params.push(`period=${this.period}`)
      }
      const query = params.join('&')

      Promise.all([
        axios.get(`controlling/overview?${query}`, { headers: this.authHeaders() }),
        axios.get(`controlling/alerts?${query}&skip_refresh=1`, { headers: this.authHeaders() }),
      ]).then(([overviewResponse, alertResponse]) => {
        this.overview = overviewResponse.data || {}
        this.alerts = alertResponse.data || []
      }).catch(() => {
        this.overview = { summary: {}, top_risks: [] }
        this.alerts = []
      }).finally(() => {
        this.loading = false
      })
    },
    loadProject(projectId) {
      this.$router.push({
        name: 'monolit.controlling.project.details',
        params: { projectId: String(projectId) },
      })
    },
    riskVariant(level) {
      if (level === 'critical') return 'danger'
      if (level === 'high') return 'warning'
      if (level === 'medium') return 'info'
      return 'success'
    },
    sourceForKpi(kpiKey) {
      const summary = this.overview?.sources?.summary || {}
      const src = summary[kpiKey] || {}
      return {
        short: src.short || 'N/A',
      }
    },
    money(value) {
      const amount = Number(value || 0)
      return `${amount.toLocaleString('hu-HU', { maximumFractionDigits: 0 })} Ft`
    },
  },
}
</script>
